import React from 'react';
import { HeroImage as HeroImageType } from './types';
import { MobileImageCarousel } from './MobileImageCarousel';
import { MAX_VEHICLE_HERO_IMAGES } from '../../constants/vehicleGallery';
import { HeroImage } from '../ui/HeroImage';
import { focalToObjectPosition } from '../../constants/imageCropPresets';

interface VehicleHeroGalleryProps {
  images: HeroImageType[];
  isOwner: boolean;
  onImageClick: (index: number) => void;
  onEditImage: (index: number) => void;
  onDeleteImage: (index: number) => void;
  onMoveImage: (fromIndex: number, toIndex: number) => void;
  onAddImage: () => void;
  /** Car-show / iPad kiosk layout — large swipeable gallery */
  showcaseMode?: boolean;
  /** When true, gallery fills a sticky side panel (landscape iPad) */
  showcasePanel?: boolean;
  /** Nested in the build-page photo card — no extra shell/border/scrim. */
  embedded?: boolean;
}

type CollageCell = {
  index: number;
  className: string;
  /** Banner aspect for the media frame */
  frameClass: string;
};

/**
 * Banner-aspect collage patterns by photo count (tablet/desktop).
 * All frames use wide ratios (2:1 / 21:9 / 16:9) rather than squares.
 */
function collageCells(count: number): { gridClass: string; cells: CollageCell[] } {
  const banner = 'aspect-[2/1]';
  const wide = 'aspect-[21/9]';
  const cinema = 'aspect-[16/9]';

  switch (count) {
    case 1:
      return {
        gridClass: 'grid-cols-1',
        cells: [{ index: 0, className: 'col-span-1', frameClass: wide }],
      };
    case 2:
      return {
        gridClass: 'grid-cols-2',
        cells: [
          { index: 0, className: 'col-span-1', frameClass: banner },
          { index: 1, className: 'col-span-1', frameClass: banner },
        ],
      };
    case 3:
      return {
        gridClass: 'grid-cols-3 grid-rows-2',
        cells: [
          { index: 0, className: 'col-span-2 row-span-2', frameClass: 'h-full min-h-[14rem] aspect-auto' },
          { index: 1, className: 'col-span-1', frameClass: banner },
          { index: 2, className: 'col-span-1', frameClass: banner },
        ],
      };
    case 4:
      return {
        gridClass: 'grid-cols-2',
        cells: [0, 1, 2, 3].map((index) => ({
          index,
          className: 'col-span-1',
          frameClass: cinema,
        })),
      };
    case 5:
      return {
        gridClass: 'grid-cols-6',
        cells: [
          { index: 0, className: 'col-span-6', frameClass: wide },
          { index: 1, className: 'col-span-3', frameClass: banner },
          { index: 2, className: 'col-span-3', frameClass: banner },
          { index: 3, className: 'col-span-3', frameClass: banner },
          { index: 4, className: 'col-span-3', frameClass: banner },
        ],
      };
    case 6:
      return {
        gridClass: 'grid-cols-3',
        cells: [0, 1, 2, 3, 4, 5].map((index) => ({
          index,
          className: 'col-span-1',
          frameClass: cinema,
        })),
      };
    case 7:
      return {
        gridClass: 'grid-cols-6',
        cells: [
          { index: 0, className: 'col-span-6', frameClass: wide },
          ...[1, 2, 3, 4, 5, 6].map((index) => ({
            index,
            className: 'col-span-2',
            frameClass: cinema,
          })),
        ],
      };
    case 8:
    default:
      return {
        gridClass: 'grid-cols-4',
        cells: Array.from({ length: Math.min(count, 8) }, (_, index) => ({
          index,
          className: 'col-span-1',
          frameClass: cinema,
        })),
      };
  }
}

function GalleryTile({
  image,
  index,
  total,
  frameClass,
  isOwner,
  onImageClick,
  onEditImage,
  onDeleteImage,
  onMoveImage,
}: {
  image: HeroImageType;
  index: number;
  total: number;
  frameClass: string;
  isOwner: boolean;
  onImageClick: (index: number) => void;
  onEditImage: (index: number) => void;
  onDeleteImage: (index: number) => void;
  onMoveImage: (fromIndex: number, toIndex: number) => void;
}) {
  return (
    <div
      className="relative group cursor-pointer min-w-0 h-full"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onImageClick(index);
      }}
    >
      <div className={`${frameClass} h-full bg-[#111] overflow-hidden`}>
        <HeroImage
          src={image.src}
          alt={image.alt}
          position={focalToObjectPosition(image.focalX, image.focalY)}
          priority={index === 0}
          className="transition-transform duration-200 group-hover:scale-[1.03]"
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-white text-sm font-medium truncate backdrop-blur-sm bg-black/30 px-2 py-1 rounded">
            {image.title}
          </h3>
        </div>
      </div>

      {isOwner ? (
        <div
          className="absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
          onClick={(e) => e.stopPropagation()}
        >
          {index > 0 ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveImage(index, index - 1);
              }}
              className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30 z-30"
              title="Move left"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : null}
          {index < total - 1 ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveImage(index, index + 1);
              }}
              className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30 z-30"
              title="Move right"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : null}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditImage(index);
            }}
            className="p-1.5 bg-[#141414]/80 backdrop-blur-sm hover:bg-white/[0.06] text-white rounded-lg transition-colors border border-white/[0.08] z-30"
            title="Edit image"
            type="button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDeleteImage(index);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="p-1.5 bg-red-500/80 backdrop-blur-sm hover:bg-red-500 text-white rounded-lg transition-colors border border-red-400/30 z-30 cursor-pointer"
            title="Delete image"
            type="button"
            style={{ pointerEvents: 'auto' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ) : null}
    </div>
  );
}

export const VehicleHeroGallery: React.FC<VehicleHeroGalleryProps> = ({
  images,
  isOwner,
  onImageClick,
  onEditImage,
  onDeleteImage,
  onMoveImage,
  onAddImage,
  showcaseMode = false,
  showcasePanel = false,
  embedded = false,
}) => {
  // Side-panel showcase stays swipeable; normal build pages: carousel on mobile only.
  const forceCarousel = showcasePanel;
  const carouselSize = showcasePanel ? 'showcase' : showcaseMode ? 'tablet' : 'default';
  const autoPlay = !isOwner;
  const collage = collageCells(images.length);
  const showAddSlot = images.length > 0 && images.length < MAX_VEHICLE_HERO_IMAGES && isOwner;

  const shellClass = showcasePanel
    ? 'h-full bg-[#0c0c0c]/50 border-b lg:border-b-0 lg:border-r border-white/[0.06] flex flex-col'
    : embedded
      ? ''
      : 'bg-[#0c0c0c]/50 border-b border-white/[0.06]';

  const innerClass = showcasePanel
    ? 'flex-1 flex flex-col px-4 sm:px-6 py-4 lg:py-6 min-h-0'
    : embedded
      ? 'px-4 sm:px-5 py-4 sm:py-5'
      : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8';

  return (
    <div className={shellClass}>
      <div className={innerClass}>
        {/* Carousel: phones only (and sticky showcase side panel) */}
        <div className={forceCarousel ? 'block' : 'block md:hidden'}>
          <MobileImageCarousel
            images={images}
            isOwner={isOwner}
            onImageClick={onImageClick}
            onEditImage={onEditImage}
            onDeleteImage={onDeleteImage}
            onMoveImage={onMoveImage}
            onAddImage={onAddImage}
            size={carouselSize}
            autoPlay={autoPlay}
          />
          {showcaseMode && !isOwner && images.length > 0 && (
            <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 md:hidden">
              Swipe for more · Tap to enlarge
            </p>
          )}
        </div>

        {/* Banner collage: tablet and up — single rounded rectangle, no gaps between photos */}
        <div className={forceCarousel ? 'hidden' : 'hidden md:block'} data-collage-count={images.length}>
          {images.length > 0 ? (
            <div
              className={`grid gap-0 overflow-hidden rounded-xl border border-white/[0.07] bg-[#111] ${collage.gridClass}`}
            >
              {collage.cells.map((cell) => {
                const image = images[cell.index];
                if (!image) return null;
                return (
                  <div key={`${image.src}-${cell.index}`} className={`min-w-0 ${cell.className}`}>
                    <GalleryTile
                      image={image}
                      index={cell.index}
                      total={images.length}
                      frameClass={cell.frameClass}
                      isOwner={isOwner}
                      onImageClick={onImageClick}
                      onEditImage={onEditImage}
                      onDeleteImage={onDeleteImage}
                      onMoveImage={onMoveImage}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}

          {showAddSlot ? (
            <button
              type="button"
              onClick={onAddImage}
              className="mt-3 w-full aspect-[2/1] max-h-36 bg-[#0c0c0c]/50 rounded-xl border-2 border-dashed border-white/[0.08] hover:border-orange-500/50 cursor-pointer transition-all duration-200 flex items-center justify-center group hover:bg-white/[0.04]/40"
            >
              <div className="text-center px-3">
                <svg
                  className="w-8 h-8 text-gray-500 group-hover:text-orange-400 mx-auto mb-2 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <p className="text-meta group-hover:text-orange-400 transition-colors">Add photo</p>
                <p className="text-[11px] text-gray-600 mt-1 tabular-nums">
                  {images.length}/{MAX_VEHICLE_HERO_IMAGES}
                </p>
              </div>
            </button>
          ) : null}

          {images.length === 0 && !isOwner ? (
            <div className="text-center py-12 card rounded-xl">
              <svg
                className="w-16 h-16 text-gray-500 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-muted">No photos available</p>
            </div>
          ) : null}

          {images.length === 0 && isOwner ? (
            <div className="text-center py-12 card rounded-xl">
              <p className="text-muted text-sm mb-4">No photos yet</p>
              <button type="button" onClick={onAddImage} className="btn-primary">
                Add photos
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
